import React, { useState } from 'react'
import { Alert, Box, Button, Dialog, DialogContent, DialogTitle, Pagination, Stack, Typography } from '@mui/material'
import { Section, Field } from './presentation'
import { useAdminData, timestamp, errorMessage } from './shared'
import { SnapshotImage } from 'modules/Account/modules/ClaimSnapshotPage'

const sourceLabel = source => source === 'REVIEW' ? 'Moderation snapshot' : 'Claim snapshot'
const dateValue = value => Array.isArray(value)
  ? `${value[0]}-${String(value[1]).padStart(2, '0')}-${String(value[2]).padStart(2, '0')}T${String(value[3] || 0).padStart(2, '0')}:${String(value[4] || 0).padStart(2, '0')}:${String(value[5] || 0).padStart(2, '0')}Z`
  : value
const safeImage = value => typeof value === 'string' && (/^\/images\//.test(value) || /^https?:\/\//.test(value)) ? value : null

function SnapshotReview({ offerId, version, onClose }) {
  const { data, error } = useAdminData(`/admin/offers/${offerId}/snapshots/${version.source}/${version.snapshot_id}`)
  const content = data?.content || {}
  const offer = content.offer || content.correctionBaseline || content
  const value = (snake, camel) => offer[snake] ?? offer[camel]
  const image = safeImage(value('offer_image', 'offerImage'))
  const fields = [
    ['Headline', value('headline_1', 'headline1')],
    ['Additional headline', value('headline_2', 'headline2')],
    ['Description', offer.description],
    ['Terms and conditions', value('terms_and_conditions', 'termsAndConditions')],
    ['Starts', timestamp(dateValue(value('start_date', 'startDate')))],
    ['Ends', timestamp(dateValue(value('end_date', 'endDate')))],
    ['Redemption type', offer.fulfillment],
    ['Promo code', value('promo_code', 'promoCode')],
    ['Barcode', offer.barcode],
    ['QR code', value('qr_code', 'qrCode')],
    ['Website', value('site_url', 'siteUrl')],
  ]
  return <Dialog open onClose={onClose} fullWidth maxWidth="md">
    <DialogTitle>{sourceLabel(version.source)} · {timestamp(version.captured_at)}</DialogTitle>
    <DialogContent>
      {error ? <Alert severity="error">{errorMessage(error)}</Alert> : !data ? <Typography>Loading historical version…</Typography> : <Stack gap={2}>
        <Alert severity="info">This is a saved historical version, not the current offer. Only captured fields are shown.</Alert>
        {content.baselineCapturedAfterPenalty && <Alert severity="warning">This correction baseline was captured before a later edit, after the original penalty. It is not a snapshot from the time of the offense.</Alert>}
        {version.source === 'CLAIM' && ['logo', 'image'].map(slot => <SnapshotImage key={`${version.snapshot_id}-${slot}`} claimId={version.snapshot_id} slot={slot} media={content[slot]} alt={slot === 'logo' ? 'Business logo' : 'Offer image'} endpoint={`/api/admin/offers/${offerId}/snapshots/CLAIM/${version.snapshot_id}/media/${slot}`} />)}
        {image && <Box component="img" src={image} alt="Historical offer reference" sx={{ maxWidth: '100%', maxHeight: 320, objectFit: 'contain', alignSelf: 'flex-start' }} />}
        {fields.map(([label, text]) => <Box key={label} sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}><Field label={label}>{text || 'Not captured'}</Field></Box>)}
        {data.offense_number != null && <Typography>Offense {data.offense_number} · Cycle {data.cycle_number}</Typography>}
        {data.content_sha256 && <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>SHA-256: {data.content_sha256}</Typography>}
        <Box component="details"><summary>View stored record</summary><Box component="pre" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{JSON.stringify(content, null, 2)}</Box></Box>
      </Stack>}
      <Button onClick={onClose} sx={{ mt: 2 }}>Close</Button>
    </DialogContent>
  </Dialog>
}

function SnapshotOccurrences({ offerId, version, onReview }) {
  const [page, setPage] = useState(0)
  const { data, error } = useAdminData(`/admin/offers/${offerId}/snapshots/${version.source}/${version.snapshot_id}/occurrences?page=${page}`)
  return <Box sx={{ pl: 2, borderLeft: 2, borderColor: 'divider' }}>
    {error ? <Alert severity="error">{errorMessage(error)}</Alert> : !data ? <Typography>Loading records…</Typography> : <Stack gap={1}>
      {data.content.map(record => <Box key={record.snapshot_id} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography>{timestamp(record.captured_at)} · Record {record.snapshot_id}</Typography>
        <Button onClick={() => onReview(record)} aria-label={`Review record ${record.snapshot_id}`}>Review record</Button>
      </Box>)}
      {!data.content.length && <Typography>No records on this page.</Typography>}
      {data.totalPages > 1 && <Pagination count={data.totalPages} page={page + 1} onChange={(_, next) => setPage(next - 1)} aria-label="Record pages" />}
    </Stack>}
  </Box>
}

function SnapshotGroup({ offerId, version, onReview }) {
  const [expanded, setExpanded] = useState(false)
  const count = version.occurrence_count || 1
  return <Stack gap={1}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <Box>
        <Typography>{sourceLabel(version.source)} · {count} {count === 1 ? 'record' : 'records'}</Typography>
        {count > 1 ? <>
          <Typography variant="body2" color="text.secondary">First: {timestamp(version.first_captured_at)}</Typography>
          <Typography variant="body2" color="text.secondary">Latest: {timestamp(version.captured_at)}</Typography>
        </> : <Typography variant="body2" color="text.secondary">{timestamp(version.captured_at)}</Typography>}
      </Box>
      <Button onClick={() => onReview(version)} aria-label={`Review ${sourceLabel(version.source).toLowerCase()} ${version.snapshot_id}`}>Review version</Button>
      {count > 1 && <Button onClick={() => setExpanded(!expanded)} aria-expanded={expanded}>{expanded ? 'Hide records' : 'Show records'}</Button>}
    </Box>
    {expanded && <SnapshotOccurrences offerId={offerId} version={version} onReview={onReview} />}
  </Stack>
}

export default function OfferSnapshotHistory({ offerId }) {
  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState(null)
  const { data, error } = useAdminData(`/admin/offers/${offerId}/snapshots?page=${page}`)
  return <Section title="Historical snapshots">
    <Typography color="text.secondary" mb={2}>Identical saved content is grouped within each source, latest capture first. Expand a group to review its individual records. These snapshots do not cover every edit.</Typography>
    {error ? <Alert severity="error">{errorMessage(error)}</Alert> : !data ? <Typography>Loading snapshots…</Typography> : <Stack gap={1}>
      {data.content.map(version => <SnapshotGroup key={`${offerId}-${version.source}-${version.snapshot_id}`} offerId={offerId} version={version} onReview={setSelected} />)}
      {!data.content.length && <Typography>No historical snapshots.</Typography>}
      {data.totalPages > 1 && <Pagination count={data.totalPages} page={page + 1} onChange={(_, next) => setPage(next - 1)} />}
    </Stack>}
    {selected && <SnapshotReview key={`${selected.source}-${selected.snapshot_id}`} offerId={offerId} version={selected} onClose={() => setSelected(null)} />}
  </Section>
}
